import React, { useContext, useMemo, useState } from 'react';
import arrayMove from 'array-move';
import {
  SortableContainer,
  SortableElement,
  SortEndHandler,
  SortStartHandler,
} from 'react-sortable-hoc';
import styled from 'styled-components';

import { List } from 'components';
import { useBreakpoint } from 'hooks';
import TourEditContext from '../TourEditContext';
import { OptimizeStatus, TourItemContent } from '../types';

import RouteListingOrStop from './RouteListingOrStop';
import RouteListDots from './RouteListDots';
import CancelledShowingsList from './CancelledShowingsList';

const RouteList: React.FC = () => {
  const {
    tourItems,
    setTourItems,
    setOptimizeStatus,
    isSorting,
    setIsSorting,
  } = useContext(TourEditContext);
  const { md } = useBreakpoint();
  const [sortingIndex, setSortingIndex] = useState<number>(-1);

  const onSortEnd: SortEndHandler = ({ oldIndex, newIndex }) => {
    const newTourItems = arrayMove(tourItems, oldIndex, newIndex);

    // Logic for reassigning nextItem property for newly arranged tour items
    const tourItemsWithOrder: TourItemContent[] = newTourItems.map(
      (item, i) => {
        const { isFirstItem, ...newItem } = item;
        newItem.nextItem = newTourItems[i + 1]?.id || null;
        if (i === 0) (newItem as TourItemContent).isFirstItem = true;
        return newItem;
      },
    );

    setTourItems(tourItemsWithOrder);
    setOptimizeStatus(OptimizeStatus.NOT_OPTIMIZED);
    setIsSorting(false);
    setSortingIndex(-1);
  };

  // update state before start to avoid conflict with react-sortable-hoc
  const onSortBeforeStart: SortStartHandler = ({ index }) => {
    setIsSorting(true);
    setSortingIndex(index);
  };

  return (
    <StyledRouteListContainer $md={md}>
      <RouteListDots>
        <SortableTourList
          tourItems={tourItems}
          updateBeforeSortStart={onSortBeforeStart}
          onSortEnd={onSortEnd}
          isSorting={isSorting}
          sortingIndex={sortingIndex}
          lockAxis="y"
          useDragHandle={md}
          pressDelay={md ? 1 : 200}
          md={md}
        />
        <CancelledShowingsList />
      </RouteListDots>
    </StyledRouteListContainer>
  );
};

export default RouteList;

const SortableTourItem = SortableElement(
  ({
    key,
    item,
    isSorting,
    isBeingSorted,
    md,
  }: {
    key: string;
    item: TourItemContent;
    isSorting: boolean;
    isBeingSorted: boolean;
    md: boolean;
  }) => (
      <StyledRouteListingOrStopContainer
        onContextMenu={e => e.preventDefault()}
        $md={md}
      >
        <RouteListingOrStop
          isSorting={isSorting}
          isBeingSorted={isBeingSorted}
          key={item.id}
          item={item}
        />
      </StyledRouteListingOrStopContainer>
    ),
);

const SortableTourList = SortableContainer(
  ({
    tourItems,
    isSorting,
    sortingIndex,
    md,
  }: {
    tourItems: TourItemContent[];
    isSorting: boolean;
    sortingIndex: number;
    md: boolean;
  }) => {
    const disableSort = useMemo(() => tourItems.length < 2, [tourItems]);

    return (
      <List split={false}>
        {tourItems.map((item, index) => (
          <SortableTourItem
            key={item.id}
            item={item}
            index={index}
            isSorting={isSorting}
            isBeingSorted={isSorting && sortingIndex === index}
            disabled={disableSort}
            md={md}
          />
        ))}
      </List>
    );
  },
);

const StyledRouteListContainer = styled.div<{ $md: boolean }>`
  position: relative;
  overflow-y: ${({ $md }) => ($md ? 'auto' : 'visible')};
  background-color: white;
`;

const StyledRouteListingOrStopContainer = styled.div<{ $md: boolean }>`
  z-index: ${({ $md }) => ($md ? 'auto' : 1000)};
`;
